/**
 * German labels of the platform enums, keyed on the generated types like `@/lib/labels`:
 * a value added in the backend is a type error here, not a blank cell.
 *
 * Kept apart from the tenant labels so everything the platform shows lives in this folder.
 */

import type { components } from "@countdown/api-client";

type Schemas = components["schemas"];

export const ENVIRONMENT_LABELS: Readonly<
  Record<Schemas["PlatformSessionView"]["environment"], string>
> = {
  development: "Entwicklung",
  staging: "Test",
  production: "Produktion",
};

export const TENANT_STATUS_LABELS: Readonly<
  Record<Schemas["TenantStatus"], string>
> = {
  provisioning: "In Einrichtung",
  active: "Aktiv",
  suspended: "Gesperrt",
  offboarded: "Abgemeldet",
};

export const PROVISIONING_STATE_LABELS: Readonly<
  Record<Schemas["ProvisioningState"], string>
> = {
  pending: "Ausstehend",
  in_progress: "Läuft",
  ready: "Bereit",
  failed: "Fehlgeschlagen",
  decommissioned: "Stillgelegt",
};

export const STEP_STATUS_LABELS: Readonly<
  Record<Schemas["ProvisioningStepStatus"], string>
> = {
  completed: "Erledigt",
  pending_manual: "Von Hand",
  failed: "Fehlgeschlagen",
};

/**
 * `provisioning.service.STEP_ORDER`, then the steps of `delete_cell` (its report and its
 * ledger row); a name outside them is shown as it comes.
 */
export const STEP_LABELS: Readonly<Record<string, string>> = {
  database_and_role: "Datenbank und Rolle",
  schema_migration: "Schema",
  object_storage_bucket: "Speicherbereich",
  identity_realm: "Identitätsraum",
  domain_registration: "Domains",
  backup_and_monitoring: "Sicherung und Überwachung",
  control_plane_activation: "Freischaltung",
  domains_removed: "Domains entfernt",
  backup_deregistration: "Sicherung abmelden",
  database_dropped: "Datenbank gelöscht",
  cell_delete: "Löschung",
};

/** `models.enums.SuppressionReason`; the verification names it as plain text. */
export const SUPPRESSION_REASON_LABELS: Readonly<Record<string, string>> = {
  unsubscribe: "Abmeldung",
  hard_bounce: "Unzustellbar",
  blocked: "Gesperrt",
  unconfirmed: "Unbestätigt",
};

export const DOMAIN_PURPOSE_LABELS: Readonly<
  Record<Schemas["DomainPurpose"], string>
> = {
  participant: "Teilnahme",
  backoffice: "Backoffice",
  sponsor_portal: "Sponsorenportal",
};

/** Concept 11.4. */
export const OPERATING_MODEL_LABELS: Readonly<
  Record<Schemas["OperatingModel"], string>
> = {
  m1_self_operated: "M1 Eigenbetrieb",
  m2_self_managed: "M2 Self-Managed",
  m3_managed: "M3 Managed",
  m4_standalone: "M4 Standalone",
};

/** The switches of `CellPolicy`, in the order the cell page shows them. */
export const POLICY_SWITCH_LABELS = {
  calendar_list: "Kalenderliste auf der Domain",
  house_font: "Hausschrift laden",
  newsletter: "Newsletter-Einwilligung erlaubt",
  participant_sign_in: "Anmelden für Teilnehmende anbieten",
} as const satisfies Partial<Record<keyof Schemas["CellPolicyView"], string>>;

export type PolicySwitch = keyof typeof POLICY_SWITCH_LABELS;

export const CELL_LOCATION_LABELS: Readonly<
  Record<Schemas["CellLocation"], string>
> = {
  s1_operator: "S1 Betreiber",
  s2_tenant: "S2 fremde Infrastruktur",
};
